/**
 * ZALT Buy Box — site default (won the June–July 2026 A/B test vs the
 * April PDP; called 2026-07-31 at 91% confidence)
 *
 * Model: Flavor (4-Can Sampler OR single flavor) → Pack size (3/5 cans,
 * fixed 4 for the sampler) → Plan (one-time / Subscribe & Save).
 *
 * Plan defaults (deliberate):
 *   - Subscribe & Save pre-selected on every tier (sampler included).
 *     Every fresh page load lands on this default.
 *   - An EXPLICIT plan pick persists across flavor/pack switches while
 *     on the page — the form never silently re-selects subscription
 *     after the visitor chose one-time.
 *
 * Displayed prices are the real Shopify variant / selling-plan-allocation
 * prices from the section's JSON blob — what you see is what /cart/add.js
 * charges. No display overrides.
 */
(function () {
  'use strict';

  var FLAVOR_CONFIG = {
    'Watermelon':      { color: '#E0524C' },
    'Cool Mint':       { color: '#6BCBE3' },
    'Cucumber Citrus': { color: '#4CAF6E' },
    'Ginger Lime':     { color: '#E8C547' }
  };

  // Canonical display order for the flavor grid + sticky dots. Sold-out
  // flavors are sorted after this (see the sort below); unknown flavors
  // fall to the end of the in-stock group.
  var FLAVOR_DISPLAY_ORDER = ['Watermelon', 'Cool Mint', 'Ginger Lime', 'Cucumber Citrus'];

  /* ------------------------------------------------------------------ */
  /* DOM refs                                                            */
  /* ------------------------------------------------------------------ */

  var jsonEl = document.querySelector('.bxb-section [data-product-json]');
  if (!jsonEl) return;

  var samplerBtn    = document.querySelector('[data-pick-sampler]');
  var flavorGrid    = document.querySelector('[data-flavor-grid]');
  var packSection   = document.querySelector('[data-pack-section]');
  var packHint      = document.querySelector('[data-pack-hint]');
  var planSection   = document.querySelector('[data-plan-section]');
  var buyBtn        = document.querySelector('[data-buy-now]');
  var ctaPriceEl    = document.querySelector('[data-cta-price]');
  var buyErrorEl    = document.querySelector('[data-buy-error]');
  var refillsNote   = document.querySelector('[data-refills-note]');
  var ctaAnchor     = document.querySelector('[data-cta-anchor]');
  var stickyBar     = document.querySelector('[data-sticky-bar]');
  var stickyConfig  = document.querySelector('[data-sticky-config]');
  var stickyPlan    = document.querySelector('[data-sticky-plan]');
  var stickyPrice   = document.querySelector('[data-sticky-price]');
  var stickyBuy     = document.querySelector('[data-sticky-buy]');
  var stickyDots    = document.querySelector('[data-sticky-dots]');
  var galleryMain   = document.querySelector('.bxb-section [data-gallery-main]');
  var galleryThumbs = document.querySelector('.bxb-section [data-gallery-thumbs]');
  var tabBtns       = document.querySelectorAll('.bxb-section [data-tab-btn]');
  var tabPanels     = document.querySelectorAll('.bxb-section [data-tab-panel]');

  /* ------------------------------------------------------------------ */
  /* Data                                                                */
  /* ------------------------------------------------------------------ */

  var raw = JSON.parse(jsonEl.textContent);
  var fivePack  = raw.fivePack;
  var fourPack  = raw.fourPack || null;
  var threePack = raw.threePack || null;
  // Standalone "Just Subscribe" page (/pages/instant-subscribe): hide the
  // one-time plan, force Subscribe & Save, relabel the CTA. Off on the PDP arm.
  var subscribeOnly = !!raw.subscribeOnly;
  // V1 sunset switch: hide Subscribe & Save entirely, every add is one-time.
  // Wins over subscribeOnly if both are set (safer failure: no accidental
  // new contracts). Existing subscribers/renewals are Skio-side, unaffected.
  var oneTimeOnly = !!raw.oneTimeOnly;
  var ctaText       = raw.ctaLabel || 'Add to Cart';
  var flavorCards = raw.flavorCards || {};
  var subPerks  = raw.subPerks || [];
  var subDiscountPct = raw.subDiscountPct != null ? raw.subDiscountPct : 30;
  var freeShipPerk = raw.freeShipPerk || 'Free shipping on every order';

  /* sub_free_shipping_test variant (Intelligems) — theme.liquid resolves the
     group onto <html> and fires `zalt:freeship` if it lands after our first
     render. Copy only; checkout shipping is the Intelligems test's job. */
  function freeShipOn() {
    return document.documentElement.classList.contains('zalt-freeship');
  }

  // Flavors staged in Shopify but not launched (no card art / no announce
  // yet) — comma-separated section setting, matched case-insensitively.
  // Filtering here hides them everywhere downstream: grid, sticky dots,
  // URL deep-links (an unmatched ?flavor falls back to the sampler).
  var hiddenFlavors = (raw.hiddenFlavors || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);

  /* Theme-level OOS override (comma-separated flavor names): the 3/5-can
     variants of these flavors render as sold out regardless of real
     Shopify inventory — stock stays untouched so Skio renewals keep
     billing. Clamped once in buildByFlavor, so every downstream check
     (tiles, tiers, CTA, sticky) sees them as unavailable. The 4-can
     sampler is its own product and is NOT affected. */
  var forceOosFlavors = (raw.forceOosFlavors || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);

  /* Same override, 3-can tier ONLY (5-can, sampler and real inventory
     untouched): the pack picker collapses to the 5-can card for these
     flavors via the existing unavailable-3-pack path in renderPacks. */
  var forceOos3packFlavors = (raw.forceOos3packFlavors || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);

  /* BOGO offer (comma-separated flavor names, e.g. "Cucumber Citrus, Ginger
     Lime"): matching tiles get a badge; picking one hides Subscribe & Save
     (one-time only) and addToCart sends quantity 2 with a hidden `_bogo`
     line property. The FREE second unit comes from a Buy X Get Y AUTOMATIC
     discount in Shopify admin — it MUST be active while this setting is
     non-empty, or customers pay for both units. Sampler and subscribe-only
     mode are never BOGO. The `_bogo` property is also what the cart drawer
     keys on to suppress its subscribe upsell on these lines. */
  var bogoFlavors = subscribeOnly ? [] : (raw.bogoFlavors || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);
  var bogoBadge    = raw.bogoBadge || 'BOGO';
  var bogoPlanTag  = raw.bogoPlanTag || 'BOGO';
  var bogoPlanName = raw.bogoPlanName || 'Buy 1, Get 1 FREE';
  var bogoPerks    = (raw.bogoPerks || []).filter(Boolean);

  function bogoFor(flavor) {
    return !!flavor && flavor !== 'sampler' && bogoFlavors.indexOf(flavor.toLowerCase()) !== -1;
  }

  function bogoActive() {
    return !isSampler() && bogoFor(state.flavor);
  }

  // Flavors come from the 5-pack's variant option1 values.
  var flavors = [];
  fivePack.variants.forEach(function (v) {
    var f = v.options[0];
    if (!f || hiddenFlavors.indexOf(f.toLowerCase()) !== -1) return;
    if (flavors.indexOf(f) === -1) flavors.push(f);
  });

  /* Tier map. byFlavor is null for the sampler (one variant for all).
     extraOosFlavors lets a tier carry its own force-OOS list on top of
     the global one (the 3-pack-only override). */
  function buildByFlavor(prod, extraOosFlavors) {
    var map = {};
    prod.variants.forEach(function (v) {
      var f = v.options[0];
      if (!f) return;
      var fl = f.toLowerCase();
      var forcedOos = forceOosFlavors.indexOf(fl) !== -1 ||
        (extraOosFlavors && extraOosFlavors.indexOf(fl) !== -1);
      map[f] = {
        id: v.id,
        price: v.price,
        available: forcedOos ? false : v.available,
        image: v.image || null,
        imageAlt: v.imageAlt || '',
        allocations: v.sellingPlanAllocations || [],
        productId: prod.id,
        productTitle: prod.title,
        title: prod.title + ' / ' + f
      };
    });
    return map;
  }

  var TIERS = {};
  if (fourPack && fourPack.variants.length > 0) {
    var sv = fourPack.variants[0];
    TIERS.sampler = {
      id: 'sampler', cans: 4, label: '4 cans', sub: 'One of each flavor',
      variant: {
        id: sv.id,
        price: sv.price,
        available: sv.available,
        image: sv.image || null,
        imageAlt: sv.imageAlt || '',
        allocations: sv.sellingPlanAllocations || [],
        productId: fourPack.id,
        productTitle: fourPack.title,
        title: fourPack.title
      }
    };
  }
  if (threePack && threePack.variants.length > 0) {
    TIERS.c3 = { id: 'c3', cans: 3, label: '3 cans', byFlavor: buildByFlavor(threePack, forceOos3packFlavors) };
  }
  TIERS.s1 = { id: 's1', cans: 5, label: '5 cans', byFlavor: buildByFlavor(fivePack) };

  /* Picking a single flavor defaults to the 5-can sleeve (Dom 2026-07-06;
     supersedes the 3-can default from 2026-06-14). The 3-can tile stays
     available as a step-down pick. Per-flavor stock is still checked in
     pickFlavor. Subscribe & Save is pre-selected on whichever tier wins
     (effectivePlan). */
  var DEFAULT_SINGLE_TIER = 's1';

  /* Order the flavor grid: canonical order first, sold-out flavors pushed
     to the end (availability is static, so this one-time sort holds).
     flavorAvailable() is a hoisted declaration and reads TIERS, built above. */
  function flavorRank(flavor) {
    var i = FLAVOR_DISPLAY_ORDER.indexOf(flavor);
    return i === -1 ? FLAVOR_DISPLAY_ORDER.length : i;
  }
  flavors.sort(function (a, b) {
    var aOut = !flavorAvailable(a), bOut = !flavorAvailable(b);
    if (aOut !== bOut) return aOut ? 1 : -1;
    return flavorRank(a) - flavorRank(b);
  });

  /* ------------------------------------------------------------------ */
  /* Skio Dynamic Build-a-Box                                            */
  /* ------------------------------------------------------------------ */

  var skioBoxProductId = raw.skioDynamicBoxProductId || '';
  var skioBoxIdPromise = null;
  if (skioBoxProductId) {
    skioBoxIdPromise = fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productPlatformId: String(skioBoxProductId) })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var box = json && (json.DynamicBox || json.dynamicBox);
        if (box && box.boxId) return box.boxId;
        console.warn('Skio: get-dynamic-box returned no boxId', json);
        return null;
      })
      .catch(function (err) {
        console.warn('Skio: get-dynamic-box failed', err);
        return null;
      });
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  /* planChoice holds an explicit pick only — it survives every flavor/pack
     switch on the page (no silent re-select mid-configuration, 006 §2.5)
     but deliberately NOT page loads: every fresh load lands on the
     Subscribe & Save default. */
  var state = {
    flavor: 'sampler', // 'sampler' | flavor name
    tier: 'sampler',   // 'sampler' | 'c3' | 's1'
    planChoice: null
  };

  function isSampler() { return state.tier === 'sampler'; }

  function currentVariant() {
    var t = TIERS[state.tier];
    if (!t) return null;
    if (t.variant) return t.variant;
    return t.byFlavor[state.flavor] || null;
  }

  /* Effective plan: Subscribe & Save pre-selected on every tier (matches
     the control arm's default — keeps the plan default constant across the
     A/B so the test reads layout, not defaults). An explicit choice always
     wins; no allocation → one-time. */
  function effectivePlan() {
    if (oneTimeOnly) return 'one-time'; // V1 sunset: no new subscriptions
    if (bogoActive()) return 'one-time'; // BOGO flavors are one-time only
    var v = currentVariant();
    var canSub = !!(v && v.allocations.length > 0);
    if (!canSub) return 'one-time';
    if (subscribeOnly) return 'subscribe';
    if (state.planChoice) return state.planChoice;
    return 'subscribe';
  }

  /* Subscribe display price. Skio selling plans are configured at 0% —
     the real 30% is a cart-level automatic discount (006 §3.2) — so
     allocation prices come back at FULL price. Display the configured
     discount off the one-time price (must match the SUBSCRIPTION DISCOUNT
     in Shopify admin); if Skio ever carries a real allocation discount,
     the lower allocation price wins. Returns null when not subscribable. */
  function subPriceCents(v) {
    if (!v || v.allocations.length === 0) return null;
    var allocPrice = v.allocations[0].price;
    if (allocPrice < v.price) return allocPrice;
    return Math.round(v.price * (1 - subDiscountPct / 100));
  }

  function currentPriceCents() {
    var v = currentVariant();
    if (!v) return 0;
    if (effectivePlan() === 'subscribe') {
      var sp = subPriceCents(v);
      if (sp != null) return sp;
    }
    return v.price;
  }

  function configLabel() {
    if (isSampler()) return '4-Can Sampler';
    return state.flavor + ' · ' + TIERS[state.tier].label;
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function fmt(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  var CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"></path></svg>';

  /* A flavor is buyable if ANY single-flavor tier (3-can or 5-can) has it
     in stock — checked per-tier so a 0-stock default tier doesn't flag the
     whole flavor sold-out. */
  function flavorAvailable(flavor) {
    var c3 = TIERS.c3 && TIERS.c3.byFlavor[flavor];
    var s1 = TIERS.s1 && TIERS.s1.byFlavor[flavor];
    return !!((c3 && c3.available) || (s1 && s1.available));
  }

  /* ------------------------------------------------------------------ */
  /* Renderers                                                           */
  /* ------------------------------------------------------------------ */

  /* Built ONCE at init; selection only toggles classes afterwards.
     Rebuilding innerHTML on every pick recreates the <img> nodes and makes
     all four card images flash. */
  function buildFlavorGrid() {
    var html = '';
    flavors.forEach(function (flavor) {
      var cfg = FLAVOR_CONFIG[flavor] || { color: '#ccc' };
      var soldOut = !flavorAvailable(flavor);
      var bogo = !soldOut && bogoFor(flavor);
      var img = flavorCards[flavor];
      html += '<div class="bxb__fcol">';
      html += '  <button type="button" class="bxb__fcard' + (soldOut ? ' is-soldout' : '') + '"'
            + ' style="--fc:' + cfg.color + ';" data-flavor="' + escapeHtml(flavor) + '"'
            + (soldOut ? ' aria-disabled="true"' : '')
            + ' aria-label="' + escapeHtml(flavor) + (soldOut ? ' — sold out' : (bogo ? ', buy one get one free' : '')) + '">';
      html += '    <span class="bxb__radio bxb__fradio" aria-hidden="true"></span>';
      if (bogo) html += '<span class="bxb__fbogo">' + escapeHtml(bogoBadge) + '</span>';
      if (img) {
        html += '    <img src="' + img + '" alt="">';
      } else {
        html += '    <span class="bxb__fdot" style="background:' + cfg.color + ';"></span>';
      }
      if (soldOut) html += '<span class="bxb__fsold">Sold out</span>';
      html += '  </button>';
      html += '  <span class="bxb__fname">' + escapeHtml(flavor) + '</span>';
      html += '</div>';
    });
    flavorGrid.innerHTML = html;

    flavorGrid.querySelectorAll('[data-flavor]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var flavor = btn.getAttribute('data-flavor');
        if (!flavorAvailable(flavor)) return;
        pickFlavor(flavor);
      });
    });
  }

  function renderFlavors() {
    flavorGrid.querySelectorAll('[data-flavor]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-flavor') === state.flavor);
    });
    samplerBtn.classList.toggle('is-active', state.flavor === 'sampler');
  }

  function renderPacks() {
    if (isSampler()) {
      packHint.style.display = 'none';
      // Sampler pack tile intentionally omits the price — it reads as the
      // fixed "what you get" row; the price lives in the CTA + sticky bar.
      packSection.innerHTML =
        '<div class="bxb__pack is-active is-fixed bxb__pack--stacked">' +
        '  <span class="bxb__pack-top">' +
        '    <span class="bxb__radio"></span>' +
        '    <span class="bxb__pack-name">4 cans</span>' +
        '  </span>' +
        '  <span class="bxb__pack-sub">One of each flavor</span>' +
        '</div>';
      return;
    }

    packHint.style.display = '';

    var v5 = TIERS.s1.byFlavor[state.flavor];
    var v3 = TIERS.c3 ? TIERS.c3.byFlavor[state.flavor] : null;

    // BOGO flavor: both pack sizes are in the offer, so both cards badge.
    var packBogo = bogoFor(state.flavor) ? '<span class="bxb__fbogo">' + escapeHtml(bogoBadge) + '</span>' : '';

    // Only show the 3/5 ladder when both tiers are buyable for this flavor.
    if (!v3 || !v3.available) {
      state.tier = 's1';
      packSection.innerHTML =
        '<div class="bxb__pack is-active is-fixed">' +
        packBogo +
        '  <span class="bxb__radio"></span>' +
        '  <span><span class="bxb__pack-name">5 cans</span></span>' +
        '  <span class="bxb__pack-total">' + (v5 ? fmt(v5.price) : '') + '</span>' +
        '</div>';
      return;
    }

    // Same card as the fixed "4 cans" row (radio + name, subtext below),
    // two-up in a row split.
    packSection.innerHTML =
      '<div class="bxb__packrow">' +
      '<button type="button" class="bxb__pack bxb__pack--stacked' + (state.tier === 'c3' ? ' is-active' : '') + '" data-tier="c3">' +
      packBogo +
      '  <span class="bxb__pack-top">' +
      '    <span class="bxb__radio"></span>' +
      '    <span class="bxb__pack-name">3 cans</span>' +
      '  </span>' +
      '  <span class="bxb__pack-sub">3 of the same flavor</span>' +
      '</button>' +
      '<button type="button" class="bxb__pack bxb__pack--stacked' + (state.tier === 's1' ? ' is-active' : '') + '" data-tier="s1">' +
      packBogo +
      '  <span class="bxb__pack-top">' +
      '    <span class="bxb__radio"></span>' +
      '    <span class="bxb__pack-name">5 cans</span>' +
      '  </span>' +
      '  <span class="bxb__pack-sub">5 of the same flavor</span>' +
      '</button>' +
      '</div>';

    packSection.querySelectorAll('[data-tier]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.tier = btn.getAttribute('data-tier');
        renderAll();
        fireViewItem();
      });
    });
  }

  function renderPlans() {
    var v = currentVariant();
    if (!v) { planSection.innerHTML = ''; return; }

    var plan = effectivePlan();
    var subPrice = subPriceCents(v);

    // BOGO flavor: one-time only — no Subscribe & Save card at all. The
    // pill carries the STORY ("We made too much"), the card title carries
    // the OFFER, the perks carry mechanics + scarcity — all editor
    // settings. Price is what's charged; strikethrough is the 2-unit value.
    if (bogoActive()) {
      var bogoHtml = '';
      bogoHtml += '<button type="button" class="bxb__plan is-active" data-plan="one-time">';
      bogoHtml += '<span class="bxb__plan-tag">' + escapeHtml(bogoPlanTag) + '</span>';
      bogoHtml += '  <span class="bxb__plan-top">';
      bogoHtml += '    <span class="bxb__radio"></span>';
      bogoHtml += '    <span class="bxb__plan-name">' + escapeHtml(bogoPlanName) + '</span>';
      bogoHtml += '    <span class="bxb__plan-pricecol">';
      bogoHtml += '      <span class="bxb__plan-price">' + fmt(v.price) + '</span>';
      bogoHtml += '      <span class="bxb__plan-was">' + fmt(v.price * 2) + '</span>';
      bogoHtml += '    </span>';
      bogoHtml += '  </span>';
      if (bogoPerks.length > 0) {
        bogoHtml += '<span class="bxb__plan-perks">';
        bogoPerks.forEach(function (p) {
          bogoHtml += '<span class="bxb__plan-perk">' + CHECK_SVG + escapeHtml(p) + '</span>';
        });
        bogoHtml += '</span>';
      }
      bogoHtml += '</button>';
      planSection.innerHTML = bogoHtml;
      return;
    }

    // Subscribe & Save renders ABOVE One-time (Dom 2026-06-14) — leads with
    // the default/preferred plan. Suppressed entirely in one-time-only mode.
    var subHtml = '';
    if (!oneTimeOnly && subPrice != null) {
      var pct = Math.round((1 - subPrice / v.price) * 100);
      subHtml += '<button type="button" class="bxb__plan' + (plan === 'subscribe' ? ' is-active' : '') + '" data-plan="subscribe">';
      if (pct > 0) subHtml += '<span class="bxb__plan-tag">' + pct + '% off' + (freeShipOn() ? ' + FREE shipping' : '') + '</span>';
      subHtml += '  <span class="bxb__plan-top">';
      subHtml += '    <span class="bxb__radio"></span>';
      subHtml += '    <span class="bxb__plan-name">Subscribe &amp; Save</span>';
      subHtml += '    <span class="bxb__plan-pricecol">';
      subHtml += '      <span class="bxb__plan-price">' + fmt(subPrice) + '</span>';
      subHtml += '      <span class="bxb__plan-was">' + fmt(v.price) + '</span>';
      subHtml += '    </span>';
      subHtml += '  </span>';
      var perks = subPerks.slice();
      if (freeShipOn()) perks.splice(Math.min(1, perks.length), 0, freeShipPerk);
      if (plan === 'subscribe' && perks.length > 0) {
        subHtml += '<span class="bxb__plan-perks">';
        perks.forEach(function (p) {
          subHtml += '<span class="bxb__plan-perk">' + CHECK_SVG + escapeHtml(p) + '</span>';
        });
        subHtml += '</span>';
      }
      subHtml += '</button>';
    }

    var oneHtml = '';
    oneHtml += '<button type="button" class="bxb__plan' + (plan === 'one-time' ? ' is-active' : '') + '" data-plan="one-time">';
    oneHtml += '  <span class="bxb__plan-top">';
    oneHtml += '    <span class="bxb__radio"></span>';
    oneHtml += '    <span class="bxb__plan-name">One-time</span>';
    oneHtml += '    <span class="bxb__plan-pricecol"><span class="bxb__plan-price">' + fmt(v.price) + '</span></span>';
    oneHtml += '  </span>';
    oneHtml += '</button>';

    planSection.innerHTML = subHtml + (subscribeOnly ? '' : oneHtml);

    planSection.querySelectorAll('[data-plan]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.planChoice = btn.getAttribute('data-plan');
        renderPlans();
        renderCta();
      });
    });
  }

  function renderCta() {
    var v = currentVariant();
    var subscribed = effectivePlan() === 'subscribe';
    if (v && v.available) {
      buyBtn.disabled = false;
      buyBtn.childNodes[0].textContent = ctaText + ' ';
      ctaPriceEl.innerHTML = '&mdash; ' + fmt(currentPriceCents());
    } else {
      buyBtn.disabled = true;
      buyBtn.childNodes[0].textContent = v ? 'Sold Out' : 'Unavailable';
      ctaPriceEl.innerHTML = '';
    }
    refillsNote.style.display = subscribed && v && v.available ? '' : 'none';
    renderSticky();
  }

  /* ------------------------------------------------------------------ */
  /* Sticky bar                                                          */
  /* ------------------------------------------------------------------ */

  function renderSticky() {
    if (!stickyBar) return;
    var v = currentVariant();
    var subscribed = effectivePlan() === 'subscribe';
    stickyConfig.textContent = configLabel();
    stickyPlan.textContent = subscribed ? 'Subscribe & Save' : (bogoActive() ? 'One-time · 2 for 1' : 'One-time');
    stickyPrice.textContent = v ? fmt(currentPriceCents()) : '';
    stickyBuy.disabled = !(v && v.available);

    // Flavor dot switcher — single-flavor mode only.
    if (isSampler()) {
      stickyDots.style.display = 'none';
    } else {
      stickyDots.style.display = '';
      var html = '<span class="bxb-sticky__dots-label">Flavor</span>';
      flavors.forEach(function (flavor) {
        var cfg = FLAVOR_CONFIG[flavor] || { color: '#ccc' };
        var soldOut = !flavorAvailable(flavor);
        html += '<button type="button" class="bxb-sticky__dot' + (state.flavor === flavor ? ' is-active' : '') + '"'
              + ' data-sticky-flavor="' + escapeHtml(flavor) + '"' + (soldOut ? ' disabled' : '')
              + ' title="' + escapeHtml(flavor) + (soldOut ? ' — sold out' : '') + '">'
              + '<span style="background:' + cfg.color + ';"></span></button>';
      });
      stickyDots.innerHTML = html;
      stickyDots.querySelectorAll('[data-sticky-flavor]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          pickFlavor(btn.getAttribute('data-sticky-flavor'));
        });
      });
    }
  }

  var stickyVisible = false;

  function updateStickyVisibility() {
    if (!stickyBar || !ctaAnchor) return;
    var drawerOpen = document.querySelector('.cart-drawer.is-open');
    var passed = ctaAnchor.getBoundingClientRect().bottom < 70;
    var show = passed && !drawerOpen;
    if (show !== stickyVisible) {
      stickyVisible = show;
      stickyBar.classList.toggle('is-visible', show);
      stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
  }

  window.addEventListener('scroll', updateStickyVisibility, { passive: true });

  /* ------------------------------------------------------------------ */
  /* Gallery (same resolution rules as the April PDP)                    */
  /* ------------------------------------------------------------------ */

  function filterImgs(arr) {
    return (arr || []).filter(function (img) { return img && img.src; });
  }
  var customGallery4Pack  = filterImgs(raw.customGallery4Pack);
  var customGallery5Pack  = filterImgs(raw.customGallery5Pack);
  var customGalleryFlavor = raw.customGalleryFlavor || {};

  function getFilteredImages() {
    if (isSampler()) {
      if (customGallery4Pack.length > 0) return customGallery4Pack;
      return fourPack && (fourPack.images || []).length > 0 ? fourPack.images : (fivePack.images || []);
    }

    var flavorImg = customGalleryFlavor[state.flavor];
    if (flavorImg || customGallery5Pack.length > 0) {
      var combined = [];
      if (flavorImg && flavorImg.src) combined.push(flavorImg);
      customGallery5Pack.forEach(function (img) {
        if (!flavorImg || img.src !== flavorImg.src) combined.push(img);
      });
      if (combined.length > 0) return combined;
    }

    // Fallback: variant image + alt-text-flavor-matched + shared images.
    var v = TIERS.s1.byFlavor[state.flavor];
    var flavorLower = state.flavor.toLowerCase();
    var productImages = fivePack.images || [];
    var variantImageUrls = {};
    fivePack.variants.forEach(function (variant) {
      if (variant.image) variantImageUrls[variant.image] = true;
    });
    var flavorNames = flavors.map(function (f) { return f.toLowerCase(); });
    var result = [];
    if (v && v.image) result.push({ src: v.image, thumb: v.image, alt: v.imageAlt || v.title });
    productImages.forEach(function (img) {
      if (variantImageUrls[img.src]) return;
      var alt = (img.alt || '').toLowerCase();
      var mentionsFlavor = flavorNames.some(function (f) { return alt.indexOf(f) !== -1; });
      if (!mentionsFlavor || alt.indexOf(flavorLower) !== -1) result.push(img);
    });
    return result.length > 0 ? result : productImages;
  }

  var lastGalleryKey = null;

  function updateGallery() {
    if (!galleryMain) return;
    var images = getFilteredImages();
    if (images.length === 0) return;

    // Same image set as last render (e.g. pack-size change within a
    // flavor) — leave the DOM alone so nothing flashes.
    var galleryKey = images.map(function (img) { return img.src; }).join('|');
    if (galleryKey === lastGalleryKey) return;
    lastGalleryKey = galleryKey;

    galleryMain.removeAttribute('srcset');
    galleryMain.removeAttribute('sizes');
    galleryMain.src = images[0].src;
    galleryMain.alt = images[0].alt || 'ZALT Electrolyte Pouches';

    if (images.length > 1) {
      var html = '';
      images.forEach(function (img, i) {
        html += '<button type="button" class="product-gallery__thumb' + (i === 0 ? ' is-active' : '') + '" data-thumb-idx="' + i + '">'
              + '<img src="' + (img.thumb || img.src) + '" alt="' + escapeHtml(img.alt || 'View ' + (i + 1)) + '" width="64" height="64" loading="lazy">'
              + '</button>';
      });
      galleryThumbs.innerHTML = html;
      galleryThumbs.style.display = '';
      galleryThumbs.querySelectorAll('[data-thumb-idx]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = parseInt(btn.getAttribute('data-thumb-idx'), 10);
          galleryMain.src = images[idx].src;
          galleryMain.alt = images[idx].alt || '';
          galleryThumbs.querySelectorAll('.product-gallery__thumb').forEach(function (t, ti) {
            t.classList.toggle('is-active', ti === idx);
          });
        });
      });
    } else {
      galleryThumbs.innerHTML = '';
      galleryThumbs.style.display = 'none';
    }
  }

  /* ------------------------------------------------------------------ */
  /* State transitions                                                   */
  /* ------------------------------------------------------------------ */

  function pickFlavor(flavor) {
    if (flavor === 'sampler') {
      state.flavor = 'sampler';
      state.tier = 'sampler';
    } else {
      state.flavor = flavor;
      // Coming from the sampler → the default single-flavor tier; otherwise
      // keep the tier the visitor already chose (3 or 5 cans).
      if (state.tier === 'sampler') state.tier = DEFAULT_SINGLE_TIER;
      // Tier may not be buyable for this flavor (e.g. 3-pack flavor OOS) —
      // fall over to the 5-can sleeve.
      var t = TIERS[state.tier];
      if (!t.byFlavor[flavor] || !t.byFlavor[flavor].available) {
        state.tier = 's1';
      }
    }
    renderAll();
    document.dispatchEvent(new CustomEvent('pdp:flavor-changed', {
      detail: { flavor: flavor === 'sampler' ? 'Variety Pack' : flavor }
    }));
    fireViewItem();
  }

  function renderAll() {
    renderFlavors();
    renderPacks();
    renderPlans();
    renderCta();
    updateGallery();
  }

  samplerBtn.addEventListener('click', function () {
    if (state.flavor !== 'sampler') pickFlavor('sampler');
  });

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */

  function getArm() {
    return (window.ZaltAB && typeof window.ZaltAB.getArm === 'function')
      ? window.ZaltAB.getArm()
      : 'drawer';
  }

  function trackEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
    } else if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  function analyticsItems(v, priceCents) {
    return [{
      item_id: v.productId,
      item_name: v.productTitle,
      item_variant: v.title,
      price: priceCents / 100,
      quantity: 1
    }];
  }

  function fireViewItem() {
    var v = currentVariant();
    if (!v) return;
    trackEvent('view_item', {
      pdp_arm: 'buybox',
      ecommerce: { currency: 'USD', value: v.price / 100, items: analyticsItems(v, v.price) }
    });
  }

  function showBuyError(msg) {
    if (!buyErrorEl) return;
    buyErrorEl.textContent = msg;
    buyErrorEl.style.display = '';
  }

  function hideBuyError() {
    if (buyErrorEl) buyErrorEl.style.display = 'none';
  }

  function addToCart(trigger) {
    var v = currentVariant();
    if (!v || !v.available) return;

    var subscribed = effectivePlan() === 'subscribe';
    var sellingPlan = subscribed && v.allocations.length > 0
      ? parseInt(v.allocations[0].sellingPlanId, 10)
      : null;
    var priceCents = currentPriceCents();

    buyBtn.disabled = true;
    var restoreLabel = function () {
      renderCta();
    };
    hideBuyError();

    trackEvent('add_to_cart', {
      arm: getArm(),
      pdp_arm: 'buybox',
      ecommerce: { currency: 'USD', value: priceCents / 100, items: analyticsItems(v, priceCents) }
    });

    function buildBody(dynamicBoxId) {
      // BOGO: two units in the cart — the Buy X Get Y automatic discount
      // zeroes the second at checkout. `_bogo` (hidden `_` property) tells
      // the cart drawer to suppress its subscribe upsell on this line.
      var bogo = !sellingPlan && bogoActive();
      var body = { id: v.id, quantity: bogo ? 2 : 1 };
      if (bogo) body.properties = { _bogo: '2-for-1' };
      if (sellingPlan) {
        body.selling_plan = sellingPlan;
        if (dynamicBoxId) {
          body.properties = { _dynamicBoxId: dynamicBoxId, _dynamicBoxIndex: 0 };
        }
      }
      return body;
    }

    var bodyPromise = sellingPlan && skioBoxIdPromise
      ? skioBoxIdPromise.then(buildBody)
      : Promise.resolve(buildBody(null));

    bodyPromise
      .then(function (body) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      })
      .then(function (r) {
        return r.json().then(function (json) { return { ok: r.ok, item: json }; });
      })
      .then(function (res) {
        if (!res.ok) {
          showBuyError((res.item && res.item.description) || 'Couldn’t add to cart. Please try again.');
          restoreLabel();
          return;
        }
        if (window.ZaltCart && typeof window.ZaltCart.openAfterAdd === 'function') {
          window.ZaltCart.openAfterAdd(res.item, { checkoutUrl: '/checkout', trigger: trigger || buyBtn });
        } else {
          window.location.href = '/checkout';
        }
        restoreLabel();
      })
      .catch(function () {
        showBuyError('Couldn’t add to cart. Please check your connection and try again.');
        restoreLabel();
      });
  }

  buyBtn.addEventListener('click', function () { addToCart(buyBtn); });
  if (stickyBuy) stickyBuy.addEventListener('click', function () { addToCart(stickyBuy); });

  /* ------------------------------------------------------------------ */
  /* Info tabs                                                           */
  /* ------------------------------------------------------------------ */

  tabBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var target = btn.getAttribute('data-tab-btn');
      tabBtns.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
      tabPanels.forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === target);
      });
    });
  });

  /* ------------------------------------------------------------------ */
  /* URL params (?flavor=cool-mint&pack=3|5|sampler&plan=sub|onetime)    */
  /* ------------------------------------------------------------------ */

  (function applyUrlParams() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }

    function normalize(s) {
      return String(s || '').toLowerCase().replace(/[\s_+-]+/g, ' ').trim();
    }

    var flavorParam = params.get('flavor');
    if (flavorParam) {
      var wanted = normalize(flavorParam);
      var match = flavors.filter(function (f) { return normalize(f) === wanted; })[0];
      if (match && flavorAvailable(match)) {
        state.flavor = match;
        state.tier = DEFAULT_SINGLE_TIER;
      }
    }

    var packParam = normalize(params.get('pack') || params.get('bundle'));
    if (packParam === '3' || packParam === '3 pack' || packParam === '3pack') {
      if (TIERS.c3 && state.flavor !== 'sampler') state.tier = 'c3';
    } else if (packParam === '5' || packParam === '5 pack' || packParam === '5pack') {
      if (state.flavor !== 'sampler') state.tier = 's1';
    } else if (packParam === '4' || packParam === '4 pack' || packParam === '4pack' || packParam === 'sampler') {
      state.flavor = 'sampler';
      state.tier = 'sampler';
    }

    var planParam = normalize(params.get('plan'));
    if (planParam === 'sub' || planParam === 'subscribe') state.planChoice = 'subscribe';
    else if (!subscribeOnly && (planParam === 'onetime' || planParam === 'one time')) state.planChoice = 'one-time';
  })();

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  buildFlavorGrid();
  renderAll();
  updateStickyVisibility();
  setTimeout(fireViewItem, 100);

  // Intelligems usually resolves after our first paint — refresh the plan
  // cards so the variant's "+ FREE shipping" tag and perk appear.
  window.addEventListener('zalt:freeship', renderPlans);
})();
